/**
 * Keyboard helpers, each owned by a Lifetime (listeners and timers go away with it).
 * Esc is NOT handled here: the overlay stack owns it.
 * Items come from `items()` on every key press, so a re-rendered list needs no re-wiring.
 * @typedef {import('./lifetime.js').Lifetime} Lifetime
 */

const NEXT = { vertical: 'ArrowDown', horizontal: 'ArrowRight' }
const PREV = { vertical: 'ArrowUp', horizontal: 'ArrowLeft' }
const TYPEAHEAD_RESET_MS = 500

const hasChord = (event) => event.ctrlKey || event.metaKey || event.altKey

/** The nearest ancestor-or-self of `target` that is one of `items` (deepest wins for nested items). */
function itemOf(items, target) {
  for (let node = target; node; node = node.parentElement) if (items.includes(node)) return node
  return null
}

function step(key, orientation) {
  const vertical = orientation !== 'horizontal'
  const horizontal = orientation !== 'vertical'
  if ((vertical && key === NEXT.vertical) || (horizontal && key === NEXT.horizontal)) return 1
  if ((vertical && key === PREV.vertical) || (horizontal && key === PREV.horizontal)) return -1
  return 0
}

/**
 * Roving tabindex: one item has tabindex 0, the rest -1; arrows/Home/End move focus, Enter/Space activate,
 * printable keys jump to the next item whose text starts with what was typed.
 * @param {Lifetime} lifetime
 * @param {Element} container
 * @param {{
 *   items: () => Element[], orientation?: 'vertical' | 'horizontal' | 'both', loop?: boolean,
 *   typeahead?: boolean, onActivate?: (item: Element, event: KeyboardEvent) => void,
 * }} options
 * @returns {{ focus: (index: number) => void, current: () => Element | null }}
 */
export function rovingFocus(lifetime, container, { items, orientation = 'vertical', loop = true, typeahead = true, onActivate } = {}) {
  let currentItem = null
  let buffer = ''
  let cancelReset = () => {}

  const sync = (list, active) => {
    for (const item of list) item.setAttribute('tabindex', item === active ? '0' : '-1')
  }
  const resolve = (list) => (list.includes(currentItem) ? currentItem : (list[0] ?? null))

  const focusItem = (list, item) => {
    if (!item) return
    currentItem = item
    sync(list, item)
    item.focus()
  }

  const moveTo = (list, index) => {
    if (list.length === 0) return
    focusItem(list, list[Math.min(Math.max(index, 0), list.length - 1)])
  }

  const search = (list, from, text) => {
    const needle = text.toLowerCase()
    for (let offset = 0; offset < list.length; offset += 1) {
      const item = list[(from + offset) % list.length]
      const label = (item.getAttribute('aria-label') ?? item.textContent ?? '').trim().toLowerCase()
      if (label.startsWith(needle)) return item
    }
    return null
  }

  const initial = items()
  currentItem = initial[0] ?? null
  sync(initial, currentItem)

  lifetime.listen(container, 'focusin', (event) => {
    const list = items()
    const item = itemOf(list, event.target)
    if (!item) return
    currentItem = item
    sync(list, item)
  })

  lifetime.listen(container, 'keydown', (event) => {
    if (hasChord(event)) return
    const list = items()
    const item = itemOf(list, event.target)
    if (!item) return
    const index = list.indexOf(item)
    const delta = step(event.key, orientation)

    if (delta) {
      event.preventDefault()
      const target = index + delta
      moveTo(list, loop ? (target + list.length) % list.length : target)
    } else if (event.key === 'Home') {
      event.preventDefault()
      moveTo(list, 0)
    } else if (event.key === 'End') {
      event.preventDefault()
      moveTo(list, list.length - 1)
    } else if ((event.key === 'Enter' || (event.key === ' ' && buffer === '')) && onActivate) {
      event.preventDefault()
      onActivate(item, event)
    } else if (typeahead && event.key.length === 1) {
      buffer += event.key
      cancelReset()
      cancelReset = lifetime.timeout(() => {
        buffer = ''
      }, TYPEAHEAD_RESET_MS)
      // A repeated single letter cycles; a longer buffer searches from the current item.
      const found = search(list, buffer.length === 1 ? index + 1 : index, buffer)
      if (found) {
        event.preventDefault()
        focusItem(list, found)
      }
    }
  })

  return {
    focus(index) {
      moveTo(items(), index)
    },
    current() {
      const list = items()
      return list.length ? resolve(list) : null
    },
  }
}

/**
 * aria-activedescendant navigation: focus stays on `input`; the active item gets `aria-selected="true"` and
 * `data-active`, the input gets `aria-activedescendant` (items must carry code-owned ids).
 * @param {Lifetime} lifetime
 * @param {HTMLElement} input
 * @param {{ items: () => Element[], onChoose?: (item: Element, event: KeyboardEvent) => void, loop?: boolean }} options
 * @returns {{ move: (delta: number) => void, reset: () => void, active: () => Element | null }}
 */
export function activeDescendant(lifetime, input, { items, onChoose, loop = true } = {}) {
  let index = -1

  const clear = (list) => {
    for (const item of list) {
      item.setAttribute('aria-selected', 'false')
      item.removeAttribute('data-active')
    }
  }

  const paint = (list) => {
    clear(list)
    const item = list[index]
    if (!item) {
      input.removeAttribute('aria-activedescendant')
      return
    }
    item.setAttribute('aria-selected', 'true')
    item.setAttribute('data-active', '')
    if (item.id) input.setAttribute('aria-activedescendant', item.id)
    item.scrollIntoView?.({ block: 'nearest' })
  }

  const api = {
    move(delta) {
      const list = items()
      if (list.length === 0) {
        index = -1
        paint(list)
        return
      }
      let next
      if (index < 0 || index >= list.length) next = delta < 0 ? list.length - 1 : 0
      else next = index + delta
      if (loop) next = ((next % list.length) + list.length) % list.length
      index = Math.min(Math.max(next, 0), list.length - 1)
      paint(list)
    },
    reset() {
      const list = items()
      index = -1
      paint(list)
    },
    active() {
      const list = items()
      return list[index] ?? null
    },
  }

  lifetime.listen(input, 'keydown', (event) => {
    if (hasChord(event)) return
    const list = items()
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      api.move(event.key === 'ArrowDown' ? 1 : -1)
    } else if (event.key === 'Home' && list.length) {
      event.preventDefault()
      index = 0
      paint(list)
    } else if (event.key === 'End' && list.length) {
      event.preventDefault()
      index = list.length - 1
      paint(list)
    } else if (event.key === 'Enter') {
      const item = list[index]
      if (!item) return
      event.preventDefault()
      onChoose?.(item, event)
    }
  })

  lifetime.add(() => input.removeAttribute('aria-activedescendant'))
  return api
}

const TREE_ITEM = '[role="treeitem"]'

/** Tree items that are visible: every ancestor treeitem is not `aria-expanded="false"`. */
function visibleTreeItems(tree) {
  return [...tree.querySelectorAll(TREE_ITEM)].filter((item) => {
    for (let parent = item.parentElement?.closest(TREE_ITEM); parent && tree.contains(parent); parent = parent.parentElement?.closest(TREE_ITEM)) {
      if (parent.getAttribute('aria-expanded') !== 'true') return false
    }
    return true
  })
}

/**
 * WAI-ARIA tree pattern on top of rovingFocus: Up/Down/Home/End/typeahead/Enter as there; Right expands a closed
 * node (onExpand) or enters an open one; Left collapses an open node (onCollapse) or goes to the parent.
 * The callbacks own `aria-expanded`; this only reads it.
 * @param {Lifetime} lifetime
 * @param {Element} tree
 * @param {{ onExpand?: (item: Element) => void, onCollapse?: (item: Element) => void, onActivate?: (item: Element, event: KeyboardEvent) => void }} options
 * @returns {{ focus: (index: number) => void, current: () => Element | null }}
 */
export function treeKeys(lifetime, tree, { onExpand, onCollapse, onActivate } = {}) {
  const roving = rovingFocus(lifetime, tree, { items: () => visibleTreeItems(tree), orientation: 'vertical', loop: false, onActivate })

  lifetime.listen(tree, 'keydown', (event) => {
    if (hasChord(event) || (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft')) return
    const list = visibleTreeItems(tree)
    const item = itemOf(list, event.target)
    if (!item) return
    const expanded = item.getAttribute('aria-expanded')
    event.preventDefault()
    if (event.key === 'ArrowRight') {
      if (expanded === 'false') onExpand?.(item)
      else if (expanded === 'true') {
        const child = list[list.indexOf(item) + 1]
        if (child && item.contains(child)) roving.focus(list.indexOf(child))
      }
    } else if (expanded === 'true') {
      onCollapse?.(item)
    } else {
      const parent = item.parentElement?.closest(TREE_ITEM)
      if (parent && tree.contains(parent)) roving.focus(list.indexOf(parent))
    }
  })

  return roving
}
